import React from 'react';
import { ArrowLeft, Handshake, Wrench, Compass, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/about.css';

const AboutPage = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <div className="about-hero">
        <div className="about-hero-content">
          <h1 className="about-hero-title">About GoCalFit</h1>
          <p className="about-hero-hook">The why behind the fly. 🪽</p>
        </div>
      </div>

      {/* Continuous Story Flow */}
      <div className="about-content">

        {/* SECTION 1 — What we believe */}
        <div className="manifesto" data-testid="about-manifesto">
          <p className="manifesto-lead"><strong>We believe movement signals growth.</strong></p>
          <p>And growth is rarely comfortable. 🫠</p>
          <p>But it can be fun while offering its challenge.</p>

          <p className="manifesto-belief">We believe people are meant to <strong>move</strong> — not just exercise.</p>
          <p className="manifesto-verbs">Move. Play. Try — no, <em>do</em> hard things. Figure stuff out.</p>

          <p>Somewhere along the way, adults tend to lose a little of the move & play — and get trapped in the "do hard things" phase.</p>
          <p>And kids aren't always given enough space to do it in the first place.</p>
          <p className="manifesto-problem"><strong>That's the problem we're interested in solving.</strong></p>

          <p className="manifesto-what">GoCalFit creates environments & offers experiences that use movement, challenge and play to build strong, capable humans.</p>

          <p><strong>For adults</strong>, we clip you into a bungee harness, tether you to the ceiling, blast the music and get you out of your head and into your body — remembering what it feels like to play again.</p>
          <p><strong>For kids</strong>, it means developing fun in a bigger way: more opportunities to move, create, explore, solve problems, take reasonable risks and discover:</p>
          <p className="manifesto-quote">"I can do this without someone — or something — doing it for me."</p>

          <div className="manifesto-triad">
            <p>Different programs. Same idea.</p>
            <p className="triad-lines"><strong>Move your body.<br />Strengthen your capability.<br />Connect as humans.</strong></p>
          </div>

          <div className="manifesto-buttons">
            <Link to="/consulting" className="about-cta-button primary" data-testid="about-move-with-us-btn">
              Move With Us
            </Link>
            <Link to="/youth" className="about-cta-button secondary" data-testid="about-youth-btn">
              See What We're Building for Kids
            </Link>
          </div>
        </div>

        {/* Photo pair — separator between the why & Cal's letter */}
        <div className="photo-pair" data-testid="about-photo-pair">
          <img 
            src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/vv970xpl_4H4A9538.JPG" 
            alt="Cal in bungee harness at GoCalFit studio" 
          />
          <img 
            src="/images/about/cal_heart_bungee.jpg" 
            alt="Cal making heart with hands while bungee flying" 
          />
        </div>

        {/* SECTION 2 — Cal's letter (boxed) */}
        <div className="cal-letter" data-testid="about-cal-letter">
          <h2 className="cal-letter-title">Hi, I'm Cal. 👋</h2>
          <p>My life started on 500 rural acres in Australia, with two teachers/hobby farmers, some amazing siblings & pigs you would have fallen in love with. And did!</p>
          <p>There was plenty of room to roam, move and play — and a built-in necessity to grow & figure things out for yourself.</p>
          <p>We were straight-up Outback. There wasn't internet access or a Home Depot around the corner.</p>
          <p className="cal-letter-punch">Or on the continent. 😂</p>
          <p>Turns out, autonomy and learning to find better ways to do & grow things out of necessity when you're young reignite pretty hard at a cellular level when you hit middle age — especially when you're simultaneously trying to figure out better ways to keep yourself from falling apart and make your kids' childhoods better.</p>
          <p>Which, I truly believe revolves around movement.</p>
          <p className="cal-letter-punch"><strong>Well. This is where I am.</strong></p>
          <p>Building opportunities for kids and adults to move, play and develop in better ways.</p>
          <p>Partly out of necessity.<br />Partly out of curiosity.</p>
          <p>If you are in need or are curious (or both!) whether there's a better way to move yourself or your kiddo(s), join in & let's figure it out together.</p>

          <div className="signature">
            <p className="signature-name">With love and energy,<br />Cal ❤️💫</p>
            <p className="signature-ps">P.S. Dad & Mum — let's be real. It was never just a hobby. 😂</p>
          </div>
        </div>

        {/* CalFit Statement */}
        <div className="calfit-statement">
          <span className="calfit-name">Core Air Life Fitness · GoCalFit · GoBungeeFit</span>
          <p className="calfit-tagline">Serious movement. Serious play.</p>
        </div>

        {/* CTA Section */}
        <div className="about-cta">
          <h2>Ready to Move?</h2>
          <div className="about-cta-buttons">
            <Link to="/bungee" className="about-cta-button primary">
              Try Bungee Fitness
            </Link>
            <Link to="/aerial" className="about-cta-button secondary">
              Explore Aerial Kids
            </Link>
            <Link to="/consulting" className="about-cta-button secondary">
              Start Here
            </Link>
          </div>
        </div>

        {/* Mission Support - Collaborations & Partnerships */}
        <div className="mission-support-section" data-testid="mission-support-section">
          <h2 className="mission-support-title">Mission Support</h2>
          <p className="mission-support-subtitle">
            Movement is a team sport. We are proud to collaborate with the best in Wilmington and beyond to keep our community flying.
          </p>
          
          <div className="partner-categories">
            {/* Strategic Partnerships */}
            <div className="partner-category">
              <div className="category-header">
                <Handshake size={24} className="category-icon" />
                <h3 className="category-title">Strategic Partnerships</h3>
              </div>
              <p className="category-description">
                The visionaries who share our mission. These collaborators help us reach more people, try bigger ideas, and create programming that wouldn't exist without them.
              </p>
              <div className="partner-logos">
                <div className="partner-logo" data-testid="partner-logo-strategic-1">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/1a0c82cg_Anytime%20Fitness%20Logo.png" 
                    alt="Anytime Fitness"
                    className="partner-logo-image"
                  />
                </div>
                <div className="partner-logo" data-testid="partner-logo-strategic-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/m6o1eppc_Honey%20Bears.png" 
                    alt="Honey Bears Picnics & Provisions"
                    className="partner-logo-image"
                  />
                </div>
                <div className="partner-logo" data-testid="partner-logo-strategic-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/6iilgbk1_iFLY_Wilmington_Logo_2024_Profile%20Logo.jpg" 
                    alt="iFLY Wilmington"
                    className="partner-logo-image"
                  />
                </div>
                <div className="partner-logo" data-testid="partner-logo-strategic-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/k6p2djje_SlingMovementLogo.png" 
                    alt="SLING Movement"
                    className="partner-logo-image"
                  />
                </div>
                <div className="partner-logo" data-testid="partner-logo-strategic-5">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/8sndh52h_Auth%20Dealer%20logo%20.jpg" 
                    alt="X-Pole Authorized Dealer"
                    className="partner-logo-image"
                  />
                </div>
              </div>
            </div>

            {/* Technical Vendors - Hidden for now
            <div className="partner-category">
              <div className="category-header">
                <Wrench size={24} className="category-icon" />
                <h3 className="category-title">Technical Vendors</h3>
              </div>
              <p className="category-description">
                The specialists behind the magic. From bungee systems to aerial rigging to safety equipment — these are the pros who make flight possible.
              </p>
              <div className="partner-logos">
                <div className="partner-logo-placeholder" data-testid="partner-logo-technical-1">
                  <span>Partner Logo</span>
                </div>
              </div>
            </div>
            */}

            {/* Guidance & Mentorship - Hidden for now
            <div className="partner-category">
              <div className="category-header">
                <Compass size={24} className="category-icon" />
                <h3 className="category-title">Guidance & Mentorship</h3>
              </div>
              <p className="category-description">
                We didn't get here alone. These are the coaches, consultants, and mentors who've shaped how we think, lead, and grow.
              </p>
              <div className="partner-logos">
                <div className="partner-logo-placeholder" data-testid="partner-logo-guidance-1">
                  <span>Partner Logo</span>
                </div>
              </div>
            </div>
            */}

            {/* The Front Line - Hidden for now
            <div className="partner-category">
              <div className="category-header">
                <Shield size={24} className="category-icon" />
                <h3 className="category-title">The Front Line</h3>
              </div>
              <p className="category-description">
                The hands that hold it all together. From our assistant coaches to our cleaning crew — these are the people behind the people. You may not see them, but you'll feel them in every detail.
              </p>
              <div className="partner-logos">
                <div className="partner-logo-placeholder" data-testid="partner-logo-frontline-1">
                  <span>Partner Logo</span>
                </div>
              </div>
            </div>
            */}
          </div>

          <p className="mission-support-cta-text">
            Interested in partnering with Go Cal Fit?{' '}
            <Link to="/consulting" className="mission-support-link">
              Let's talk <ArrowRight size={14} />
            </Link>
          </p>
        </div>

        {/* Location Section with Map */}
        <div className="about-location">
          <h2>Find Us</h2>
          <div className="location-info">
            <p className="location-name">The Studio inside Anytime Fitness</p>
            <p className="location-address">5060 New Centre Dr, Ste 70 · Wilmington, NC 28403</p>
          </div>
          <div className="location-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3299.0876!2d-77.8606!3d34.2261!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89a9f5a1c7c7c7c7%3A0x0!2s5060%20New%20Centre%20Dr%2C%20Wilmington%2C%20NC%2028403!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="300"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="GoCalFit Studio Location"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
